import { CompanyRoles } from '@domain/company';
import { type PostHog } from 'posthog-js';

export const COMPANY_GROUP_TYPE = 'company';
export const PERSON_FINGERPRINT_STORAGE_KEY = 'ph_company_person_fingerprint';
// v2: group properties gained `id` (flag release conditions match on group
// properties, not the group key) — bump invalidates already-sent fingerprints.
export const GROUP_FINGERPRINT_STORAGE_KEY = 'ph_company_group_fingerprint_v2';

const readFingerprint = (key: string): string | null => {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeFingerprint = (key: string, fingerprint: string): void => {
  try {
    globalThis.localStorage.setItem(key, fingerprint);
  } catch {
    // Storage unavailable: the event was already sent, worst case it is resent next load.
  }
};

/**
 * Associates the session with the company group and stamps company_id/company_role
 * on every captured event. group() and register() are local-only (no events captured),
 * so re-running them on every auth-context refetch is free and keeps state correct
 * after posthog.reset().
 */
export const identifyCompanyContext = (
  posthog: PostHog,
  { userId, companyId, role }: { userId: string; companyId: number; role: CompanyRoles }
): void => {
  posthog.group(COMPANY_GROUP_TYPE, companyId.toString());
  posthog.register({ company_id: companyId, company_role: role });

  // setPersonProperties() captures a billable $set event, so it only runs when the
  // user/company/role combination changes — never per page load or navigation.
  const fingerprint = `${userId}:${companyId}:${role}`;
  if (readFingerprint(PERSON_FINGERPRINT_STORAGE_KEY) === fingerprint) return;
  posthog.setPersonProperties({ company_id: companyId, company_role: role });
  writeFingerprint(PERSON_FINGERPRINT_STORAGE_KEY, fingerprint);
};

/**
 * Sets the company group display properties. group() with properties captures a
 * billable $groupidentify event, so it only fires when the display data changes.
 */
export const identifyCompanyGroupProperties = (
  posthog: PostHog,
  { companyId, name, commercialName }: { companyId: number; name: string; commercialName: string }
): void => {
  posthog.group(COMPANY_GROUP_TYPE, companyId.toString());

  const fingerprint = `${companyId}:${name}:${commercialName}`;
  if (readFingerprint(GROUP_FINGERPRINT_STORAGE_KEY) === fingerprint) return;
  posthog.group(COMPANY_GROUP_TYPE, companyId.toString(), { id: companyId, name, commercial_name: commercialName });
  writeFingerprint(GROUP_FINGERPRINT_STORAGE_KEY, fingerprint);
};

/**
 * Detaches subsequent events from any company (select-venue, user settings).
 * Local-only, captures no events. Logout is covered separately by posthog.reset().
 */
export const clearCompanyContext = (posthog: PostHog): void => {
  posthog.resetGroups();
  posthog.unregister('company_id');
  posthog.unregister('company_role');
};
